import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import GuideSpotlight from "./GuideSpotlight";
import { waitForElement, suppressAzPopup, clearAzPopup } from "./guideUtils";
import GuidePopup from "./GuidePopup";

// Step 4 of the /home roadmap: "Do a storefront scan".
// Triggered by arriving on /retail?guide=storefront.
//
//   - Starter plan  -> straight to /storefront-scanner.
//   - Every other plan (Advanced/Pro/Master) and free trial -> a /retail
//     walkthrough:
//       0. The advanced-data expander (.advExpander)
//       1. The storefront "stalk" search icon (.storefrontStalkIcon)
//     Clicking that icon opens /storefront-scanner in a NEW TAB; we drop a
//     localStorage handoff flag so that tab highlights the SCAN button
//     (see StorefrontScanGuideTrigger).
//
// Mobile has no equivalent table controls -> one popup, then /storefront-scanner.

// Only the exact "Starter Plan" is routed to /storefront-scanner.
const isStarterOnly = (lvl) => lvl === "Starter Plan";
const SCAN_FLAG = "nepeto_scan_guide";

function StorefrontGuideTrigger({ isMobile, isAuthenticated, isLoading, activePlanLevel, activePlanLoaded }) {
  const location = useLocation();
  const navigate = useNavigate();

  // Desktop walkthrough step: -1 off, 0 expander, 1 stalk icon
  const [desktopStep, setDesktopStep] = useState(-1);
  const [mobileStep, setMobileStep] = useState(0);

  const startedRef = useRef(false);
  const planRef = useRef(activePlanLevel);
  planRef.current = activePlanLevel;
  const loadedRef = useRef(activePlanLoaded);
  loadedRef.current = activePlanLoaded;
  // Mirror of desktopStep for use inside event handlers (no stale closures).
  const desktopStepRef = useRef(-1);
  desktopStepRef.current = desktopStep;
  // The single "wait for element" poll; kept in a ref so it's always cleanable.
  const pollRef = useRef(null);
  const clearPoll = () => {
    if (pollRef.current) {
      pollRef.current(); // cancel the waitForElement poll
      pollRef.current = null;
    }
  };

  const endGuide = () => {
    clearPoll();
    setDesktopStep(-1);
    startedRef.current = false;
    clearAzPopup();
  };

  const startNonStarterDesktop = () => {
    clearPoll();
    pollRef.current = waitForElement(".advExpander", {
      tries: 40,
      interval: 500,
      onFound: () => {
        clearPoll();
        setDesktopStep(0);
      },
      onTimeout: () => {
        // No expander ever mounted — tear down cleanly. endGuide() also clears the
        // azpopup suppress flag, which the bare reset used to leak. (F3)
        endGuide();
      },
    });
  };

  // Stop all polling if the component ever unmounts.
  useEffect(() => () => clearPoll(), []);

  // --- Start / branch on /retail?guide=storefront ---
  useEffect(() => {
    if (startedRef.current) return;
    const params = new URLSearchParams(location.search);
    if (params.get("guide") !== "storefront") return;
    if (location.pathname !== "/retail") return;
    if (isLoading || !isAuthenticated) return;

    startedRef.current = true;
    // Keep the auto Amazon-sync popup from covering the walkthrough.
    suppressAzPopup();
    // Strip the param from the URL bar only (not the router location) so it
    // won't disturb the page mid-walkthrough.
    window.history.replaceState({}, "", "/retail");

    // Branch only once /active_plan has actually answered. A free trial answers
    // with an empty level, which is also the pre-fetch value — so waiting on
    // `activePlanLevel` alone would send a Starter user whose fetch was merely
    // slow (>~4.5s) into the free-trial walkthrough instead of the scanner.
    // The try cap is only a backstop for a fetch that never resolves at all.
    let tries = 0;
    const decide = setInterval(() => {
      tries += 1;
      if (!loadedRef.current && tries <= 60) return;

      clearInterval(decide);
      if (isStarterOnly(planRef.current)) {
        startedRef.current = false;
        clearAzPopup();
        // Hand off to the scanner guide so Starter gets the SCAN spotlight too
        // (same-tab nav; StorefrontScanGuideTrigger consumes this on mount). (C6)
        localStorage.setItem(SCAN_FLAG, String(Date.now()));
        navigate("/storefront-scanner");
      } else {
        // Any other plan, a free trial, or a plan fetch that never landed.
        if (isMobile) setMobileStep(1);
        else startNonStarterDesktop();
      }
    }, 300);
    return () => clearInterval(decide);
  }, [location, isMobile, isAuthenticated, isLoading, navigate]);

  // Leaving /retail ends the walkthrough so its spotlight can't linger or
  // re-appear on another products page (.advExpander exists on all of them).
  useEffect(() => {
    if (location.pathname !== "/retail") {
      startedRef.current = false;
      clearPoll();
      setDesktopStep(-1);
      setMobileStep(0);
      clearAzPopup();
      // Abandoned before clicking the magnifier -> disarm the new-tab handoff.
      // (A real magnifier click opens the scanner in a NEW tab, so this same-tab
      // navigation reset never races the legit handoff.)
      if (desktopStepRef.current === 1) localStorage.removeItem(SCAN_FLAG);
    }
  }, [location.pathname]);

  // Drop the new-tab handoff flag once the stalk step is showing, so clicking
  // the magnifier (which opens /storefront-scanner in a new tab) hands off.
  useEffect(() => {
    if (desktopStep === 1) localStorage.setItem(SCAN_FLAG, String(Date.now()));
  }, [desktopStep]);

  // --- Advance to the stalk step when the expander is actually clicked ---
  useEffect(() => {
    if (isMobile) return;
    const onExpanded = () => {
      if (desktopStepRef.current !== 0) return;
      clearPoll();
      // Hide the expander spotlight while the offers panel loads (the clicked
      // expander unmounts, so keeping step 0 up would re-anchor to the wrong
      // row and — with the blocking shade — trap the user).
      setDesktopStep(-2);
      // Wait for the stalk icon in the expanded offers panel, then advance.
      // If it never appears (all offers are Amazon's, or a slow/failed load),
      // end the guide cleanly instead of leaving a dead overlay.
      pollRef.current = waitForElement(".storefrontStalkIcon", {
        tries: 60,
        interval: 300,
        onFound: () => {
          clearPoll();
          setTimeout(() => setDesktopStep(1), 300);
        },
        onTimeout: () => {
          clearPoll();
          endGuide();
        },
      });
    };
    window.addEventListener("nepeto:advExpanded", onExpanded);
    return () => window.removeEventListener("nepeto:advExpanded", onExpanded);
  }, [isMobile]);

  // --- Real magnifier click: a new tab opened -> end the /retail guide (keep
  //     the flag so the new tab can consume it). ---
  useEffect(() => {
    if (isMobile) return;
    const onStalk = () => {
      setDesktopStep((s) => (s === 1 ? -1 : s));
      startedRef.current = false;
      // Guide handed off to the new scanner tab; this /retail tab won't navigate,
      // so clear the azpopup suppress flag here or it stays stuck. Keep SCAN_FLAG. (F4)
      clearAzPopup();
    };
    window.addEventListener("nepeto:storefrontStalk", onStalk);
    return () => window.removeEventListener("nepeto:storefrontStalk", onStalk);
  }, [isMobile]);

  // ---------- Render ----------
  if (!isMobile) {
    if (desktopStep === 0) {
      return (
        <GuideSpotlight
          targetSelector=".advExpander"
          title="See the full product info"
          content="Click here to see estimated monthly sales, historical data, graphs, and more for this product."
          nextLabel="Got it"
          onNext={() => {
            const el = document.querySelector(".advExpander");
            if (el) el.click(); // expands -> nepeto:advExpanded advances us
          }}
          onSkip={endGuide}
          isMobile={false}
        />
      );
    }
    if (desktopStep === 1) {
      return (
        <GuideSpotlight
          targetSelector=".storefrontStalkIcon"
          title="Stalk the seller's storefront"
          content="Click this magnifying glass to stalk the storefront of the seller selling this product — you'll see every product they sell and spot deals among them."
          nextLabel="Got it"
          onNext={() => {
            localStorage.removeItem(SCAN_FLAG); // dismissed without opening a tab
            endGuide();
          }}
          onBack={() => {
            localStorage.removeItem(SCAN_FLAG);
            setDesktopStep(0);
          }}
          onSkip={() => {
            localStorage.removeItem(SCAN_FLAG);
            endGuide();
          }}
          isMobile={false}
        />
      );
    }
    return null;
  }

  // Mobile popup
  if (mobileStep === 0) return null;
  return (
    <GuidePopup
      dismissOnBackground={false}
      onDismiss={() => {
        setMobileStep(0);
        navigate("/storefront-scanner");
      }}
    >
      Storefront scanning lets you see <strong>every product a seller offers</strong>{" "}
      and spot deals among them. Tap below to open the{" "}
      <strong style={{ color: "#8c3e7c" }}>Storefront Scanner</strong>.
    </GuidePopup>
  );
}

export default StorefrontGuideTrigger;
